import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  User, Mail, Phone, MapPin, Globe, Lock,
  Eye, EyeOff, CheckCircle, AlertTriangle,
  Shield, RefreshCw, BadgeCheck
} from 'lucide-react';
import { updateProfile, changePassword } from '../api/auth';
import useAuthStore from '../store/authStore';

/* ─── PALETTE ─── */
const C = {
  sand:      '#f5ede0', sandDark: '#e8d8c0',
  terra:     '#c2521a', terraLt:  '#e0784a', terraDk: '#8a3510',
  forest:    '#1a3d28', forestMid:'#244d35', forestLt:'#2f6645',
  gold:      '#c8902a', goldLt:   '#e8b84a',
  ink:       '#0e1a10', mist:     '#6e8070', cream:   '#faf6ef',
};

const ROLE_STYLE = {
  ADMIN:      { bg:'rgba(194,82,26,.1)',  color:C.terraDk,  label:'Administrateur' },
  AGENT:      { bg:'rgba(26,61,40,.1)',   color:C.forestLt, label:'Agent enquêteur' },
  MANDATAIRE: { bg:'rgba(200,144,42,.1)', color:'#8a6010',  label:'Mandataire' },
  VENDEUR:    { bg:'rgba(42,143,168,.1)', color:'#1a6a82',  label:'Vendeur' },
};

/* ─── CSS ─── */
const css = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400;1,600&family=DM+Sans:wght@300;400;500;600;700&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
body{font-family:'DM Sans',sans-serif;}

.pf-page{ min-height:100vh; padding:32px 40px 60px; }
@media(max-width:768px){ .pf-page{ padding:18px 16px 48px; } }

/* ── HEADER ── */
.pf-header{ margin-bottom:32px; }
.pf-eyebrow{ font-size:.72rem;font-weight:700;letter-spacing:.18em;text-transform:uppercase;color:${C.terra};margin-bottom:7px; }
.pf-title{ font-family:'Cormorant Garamond',serif;font-size:clamp(1.8rem,3vw,2.6rem);font-weight:700;line-height:1.05;letter-spacing:-.035em;color:${C.ink}; }
.pf-title em{ font-style:italic;color:${C.terra}; }
.pf-sub{ margin-top:5px;font-size:.87rem;color:${C.mist}; }

/* ── LAYOUT ── */
.pf-grid{ display:grid; grid-template-columns:280px 1fr; gap:24px; align-items:start; }
@media(max-width:960px){ .pf-grid{ grid-template-columns:1fr; } }

/* ── AVATAR CARD ── */
.avatar-card{
  background:white; border-radius:24px;
  border:1px solid rgba(26,61,40,.07);
  box-shadow:0 6px 28px rgba(26,61,40,.07);
  overflow:hidden; position:sticky; top:84px;
}
.avatar-hero{
  height:100px;
  background:linear-gradient(135deg, ${C.forest} 0%, ${C.forestMid} 40%, ${C.terra} 100%);
  position:relative;
}
.avatar-hero-pattern{
  position:absolute; inset:0;
  background-image:
    repeating-linear-gradient(0deg, rgba(245,237,224,.04) 0, rgba(245,237,224,.04) 1px, transparent 1px, transparent 40px),
    repeating-linear-gradient(90deg, rgba(245,237,224,.04) 0, rgba(245,237,224,.04) 1px, transparent 1px, transparent 40px);
}
.avatar-wrap{
  position:relative; margin:-44px auto 0;
  width:88px; height:88px; z-index:2;
  display:block;
}
.avatar-circle{
  width:88px; height:88px; border-radius:50%;
  background:linear-gradient(135deg, ${C.terra}, ${C.gold});
  display:grid; place-items:center;
  font-family:'Cormorant Garamond',serif;
  font-size:2.4rem; font-weight:700; color:white;
  border:4px solid white;
  box-shadow:0 8px 24px rgba(194,82,26,.3);
  position:relative;
}
.avatar-online{
  position:absolute; bottom:4px; right:4px;
  width:14px; height:14px; border-radius:50%;
  background:${C.forestLt}; border:2.5px solid white;
}
.avatar-body{ padding:16px 20px 22px; text-align:center; }
.avatar-name{
  font-family:'Cormorant Garamond',serif;
  font-size:1.35rem; font-weight:700; color:${C.ink};
  margin-bottom:4px; letter-spacing:-.02em;
}
.avatar-username{ font-size:.78rem; color:${C.mist}; margin-bottom:12px; }
.role-badge{
  display:inline-flex; align-items:center; gap:6px;
  padding:6px 14px; border-radius:999px;
  font-size:.74rem; font-weight:700;
  margin-bottom:16px;
}
.avatar-divider{ height:1px; background:rgba(26,61,40,.07); margin:0 0 16px; }
.avatar-stats{ display:grid; grid-template-columns:1fr 1fr; gap:10px; }
.ast-item{ background:${C.cream}; border-radius:12px; padding:10px 8px; text-align:center; border:1px solid rgba(26,61,40,.06); }
.ast-val{ font-family:'Cormorant Garamond',serif; font-size:1.3rem; font-weight:700; color:${C.ink}; }
.ast-lbl{ font-size:.66rem; color:${C.mist}; font-weight:600; margin-top:1px; letter-spacing:.04em; text-transform:uppercase; }

/* ── RIGHT STACK ── */
.right-stack{ display:grid; gap:20px; }

/* ── SECTION CARD ── */
.sc{
  background:white; border-radius:22px;
  border:1px solid rgba(26,61,40,.07);
  box-shadow:0 4px 20px rgba(26,61,40,.05);
  overflow:hidden;
}
.sc-head{
  padding:19px 24px 16px;
  border-bottom:1px solid rgba(26,61,40,.06);
  display:flex; align-items:center; gap:11px;
  background:linear-gradient(135deg, rgba(26,61,40,.02), transparent);
}
.sc-icon{
  width:36px; height:36px; border-radius:10px;
  display:grid; place-items:center; flex:none;
}
.sc-title{ font-family:'Cormorant Garamond',serif; font-size:1.15rem; font-weight:700; color:${C.ink}; }
.sc-body{ padding:22px 24px; }

/* ── FORM FIELDS ── */
.fields-grid{ display:grid; grid-template-columns:1fr 1fr; gap:16px; margin-bottom:20px; }
@media(max-width:600px){ .fields-grid{ grid-template-columns:1fr; } }
.field-full{ grid-column:1/-1; }

.field{ display:grid; gap:6px; }
.field-lbl{
  font-size:.68rem; font-weight:800;
  letter-spacing:.14em; text-transform:uppercase;
  color:${C.mist}; display:flex; align-items:center; gap:6px;
}
.field-wrap{ position:relative; }
.field-icon{
  position:absolute; left:13px; top:50%; transform:translateY(-50%);
  color:rgba(110,128,112,.45); pointer-events:none; flex:none;
}
.field-input{
  width:100%; border:1.5px solid rgba(26,61,40,.11);
  border-radius:12px; background:${C.cream}; color:${C.ink};
  padding:11px 14px 11px 40px;
  font:500 .92rem 'DM Sans',sans-serif;
  outline:none; transition:border-color .2s, box-shadow .2s, background .2s;
}
.field-input:focus{
  border-color:${C.terra};
  box-shadow:0 0 0 4px rgba(194,82,26,.1);
  background:white;
}
.field-input:hover:not(:focus){ border-color:rgba(26,61,40,.2); background:white; }
.field-input::placeholder{ color:rgba(110,128,112,.38); }

/* password toggle */
.pwd-toggle{
  position:absolute; right:12px; top:50%; transform:translateY(-50%);
  background:transparent; border:none; cursor:pointer;
  color:${C.mist}; padding:3px; border-radius:5px;
  transition:color .2s; display:flex; align-items:center;
}
.pwd-toggle:hover{ color:${C.forest}; }
.field-input.no-icon{ padding-left:14px; }

/* strength bar */
.pwd-strength{ margin-top:6px; display:grid; gap:5px; }
.strength-track{
  height:3px; border-radius:999px;
  background:rgba(26,61,40,.08); overflow:hidden;
}
.strength-fill{ height:100%; border-radius:999px; transition:width .4s ease, background .4s ease; }
.strength-label{ font-size:.68rem; font-weight:600; }

/* ── BUTTONS ── */
.btn-save{
  display:inline-flex; align-items:center; gap:8px;
  padding:12px 26px; border-radius:12px;
  background:${C.forest}; color:${C.sand};
  font:700 .9rem 'DM Sans',sans-serif;
  border:none; cursor:pointer;
  box-shadow:0 8px 24px rgba(26,61,40,.2);
  transition:transform .25s, box-shadow .25s;
  position:relative; overflow:hidden;
}
.btn-save::before{
  content:''; position:absolute; inset:0;
  background:linear-gradient(90deg,transparent,rgba(255,255,255,.08),transparent);
  transform:translateX(-100%); transition:transform .5s;
}
.btn-save:hover::before{ transform:translateX(100%); }
.btn-save:hover:not(:disabled){ transform:translateY(-2px); box-shadow:0 14px 32px rgba(26,61,40,.26); }
.btn-save:disabled{ opacity:.5; cursor:not-allowed; transform:none; }

.btn-pwd{
  display:flex; align-items:center; justify-content:center; gap:8px;
  width:100%; padding:12px; border-radius:12px;
  background:transparent; color:${C.forest};
  font:700 .9rem 'DM Sans',sans-serif;
  border:1.5px solid rgba(26,61,40,.18); cursor:pointer;
  transition:background .2s, border-color .2s, transform .2s;
  position:relative; overflow:hidden;
}
.btn-pwd:hover:not(:disabled){
  background:rgba(26,61,40,.04);
  border-color:${C.forest};
  transform:translateY(-1px);
}
.btn-pwd:disabled{ opacity:.5; cursor:not-allowed; }

/* ── ALERTS ── */
.alert{
  display:flex; align-items:flex-start; gap:10px;
  border-radius:14px; padding:13px 16px;
  font-size:.86rem; line-height:1.65;
  position:relative; overflow:hidden;
}
.alert::before{
  content:''; position:absolute;
  left:0; top:0; bottom:0; width:3px;
}
.alert-success{
  background:rgba(47,102,69,.07);
  border:1px solid rgba(47,102,69,.2);
  color:${C.forestLt};
}
.alert-success::before{ background:${C.forestLt}; }
.alert-error{
  background:rgba(194,82,26,.07);
  border:1px solid rgba(194,82,26,.2);
  color:${C.terraDk};
}
.alert-error::before{ background:${C.terra}; }
.alert-close{
  margin-left:auto; background:transparent; border:none;
  cursor:pointer; padding:2px; border-radius:4px;
  opacity:.6; transition:opacity .2s; flex:none;
}
.alert-close:hover{ opacity:1; }

/* ── ACCOUNT INFO ── */
.info-rows{ display:grid; gap:0; }
.info-row{
  display:flex; align-items:center; gap:12px;
  padding:12px 0;
  border-bottom:1px solid rgba(26,61,40,.06);
}
.info-row:last-child{ border-bottom:none; padding-bottom:0; }
.info-row:first-child{ padding-top:0; }
.info-row-icon{
  width:32px; height:32px; border-radius:9px;
  display:grid; place-items:center; flex:none;
}
.info-row-label{ font-size:.72rem; font-weight:700; letter-spacing:.1em; text-transform:uppercase; color:${C.mist}; }
.info-row-value{ font-size:.9rem; font-weight:600; color:${C.ink}; margin-top:2px; }

/* spin */
.spin{ animation:spin .8s linear infinite; }
@keyframes spin{ to{ transform:rotate(360deg); } }
`;

/* ── STAGGER ── */
const STAGGER = { hidden:{}, visible:{ transition:{ staggerChildren:.07 } } };
const ITEM    = { hidden:{opacity:0,y:16}, visible:{opacity:1,y:0,transition:{duration:.5,ease:[.22,1,.36,1]}} };

/* ── PASSWORD STRENGTH ── */
function pwdStrength(p) {
  if (!p) return { pct:0, label:'', color:'transparent' };
  let score = 0;
  if (p.length >= 8)  score++;
  if (p.length >= 12) score++;
  if (/[A-Z]/.test(p)) score++;
  if (/[0-9]/.test(p)) score++;
  if (/[^A-Za-z0-9]/.test(p)) score++;
  if (score <= 1) return { pct:20,  label:'Très faible', color:'#e05050' };
  if (score === 2) return { pct:40,  label:'Faible',      color:C.terra   };
  if (score === 3) return { pct:65,  label:'Moyen',       color:C.gold    };
  if (score === 4) return { pct:85,  label:'Fort',        color:C.forestLt};
  return               { pct:100, label:'Très fort',   color:C.forestMid};
}

/* ── FIELD COMPONENT ── */
function Field({ label, icon:Icon, name, value, onChange, type='text', placeholder='' }) {
  const [show, setShow] = useState(false);
  const isPwd = type === 'password';
  return (
    <div className="field">
      <label className="field-lbl">{label}</label>
      <div className="field-wrap">
        {Icon && <Icon size={14} className="field-icon"/>}
        <input
          className={`field-input${!Icon?' no-icon':''}`}
          type={isPwd ? (show ? 'text' : 'password') : type}
          name={name} value={value}
          onChange={onChange}
          placeholder={placeholder}
          autoComplete={isPwd ? 'current-password' : 'off'}
        />
        {isPwd && (
          <button type="button" className="pwd-toggle" onClick={()=>setShow(v=>!v)}>
            {show ? <EyeOff size={15}/> : <Eye size={15}/>}
          </button>
        )}
      </div>
    </div>
  );
}

/* ─── MAIN ─── */
export default function Profile() {
  const { user, fetchProfile } = useAuthStore();

  const [form, setForm] = useState({
    first_name: user?.first_name || '',
    last_name:  user?.last_name  || '',
    email:      user?.email      || '',
    telephone:  user?.telephone  || '',
    pays:       user?.pays       || '',
    ville:      user?.ville      || '',
  });
  const [pwd, setPwd]     = useState({ old_password:'', new_password:'' });
  const [msg, setMsg]     = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState('');

  const onChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleProfile = async (e) => {
    e.preventDefault();
    setLoading('profile'); setMsg(null); setError(null);
    try {
      await updateProfile(form);
      await fetchProfile();
      setMsg('Profil mis à jour avec succès.');
    } catch {
      setError('Erreur lors de la mise à jour.');
    } finally { setLoading(''); }
  };

  const handlePwd = async (e) => {
    e.preventDefault();
    if (!pwd.old_password || !pwd.new_password) return;
    setLoading('pwd'); setMsg(null); setError(null);
    try {
      await changePassword(pwd);
      setMsg('Mot de passe modifié avec succès.');
      setPwd({ old_password:'', new_password:'' });
    } catch (err) {
      setError(err.response?.data?.old_password?.[0] || 'Mot de passe incorrect.');
    } finally { setLoading(''); }
  };

  const initials = ((user?.first_name?.[0]||'')+(user?.last_name?.[0]||user?.username?.[0]||'U')).toUpperCase();
  const role = ROLE_STYLE[user?.role] || { bg:'rgba(110,128,112,.1)', color:C.mist, label: user?.role };
  const strength = pwdStrength(pwd.new_password);

  return (
    <div className="pf-page">
      <style>{css}</style>

      <motion.div variants={STAGGER} initial="hidden" animate="visible">

        {/* ── HEADER ── */}
        <motion.div variants={ITEM} className="pf-header">
          <div className="pf-eyebrow">Compte</div>
          <h1 className="pf-title">Mon <em>profil</em></h1>
          <p className="pf-sub">Gérez vos informations personnelles et la sécurité de votre compte.</p>
        </motion.div>

        {/* ── ALERTS ── */}
        <AnimatePresence>
          {msg && (
            <motion.div className="alert alert-success"
              initial={{opacity:0,height:0}} animate={{opacity:1,height:'auto'}}
              exit={{opacity:0,height:0}} style={{marginBottom:20}}>
              <CheckCircle size={16} style={{flexShrink:0,marginTop:2}}/>
              {msg}
              <button className="alert-close" onClick={()=>setMsg(null)}>✕</button>
            </motion.div>
          )}
          {error && (
            <motion.div className="alert alert-error"
              initial={{opacity:0,height:0}} animate={{opacity:1,height:'auto'}}
              exit={{opacity:0,height:0}} style={{marginBottom:20}}>
              <AlertTriangle size={16} style={{flexShrink:0,marginTop:2}}/>
              {error}
              <button className="alert-close" onClick={()=>setError(null)}>✕</button>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="pf-grid">

          {/* ════ AVATAR CARD ════ */}
          <motion.div variants={ITEM}>
            <div className="avatar-card">
              <div className="avatar-hero">
                <div className="avatar-hero-pattern"/>
              </div>
              <div style={{display:'flex',justifyContent:'center'}}>
                <div className="avatar-wrap">
                  <div className="avatar-circle">
                    {initials}
                    <div className="avatar-online"/>
                  </div>
                </div>
              </div>
              <div className="avatar-body">
                <div className="avatar-name">{user?.first_name} {user?.last_name}</div>
                <div className="avatar-username">@{user?.username}</div>
                <span className="role-badge" style={{background:role.bg,color:role.color}}>
                  <BadgeCheck size={12}/> {role.label}
                </span>
                <div className="avatar-divider"/>
                <div style={{fontSize:'.74rem',color:C.mist,marginBottom:12,display:'flex',alignItems:'center',gap:6,justifyContent:'center'}}>
                  <Mail size={12}/> {user?.email||'—'}
                </div>
                <div className="avatar-stats">
                  <div className="ast-item">
                    <div className="ast-val">{user?.pays||'—'}</div>
                    <div className="ast-lbl">Pays</div>
                  </div>
                  <div className="ast-item">
                    <div className="ast-val">{user?.ville||'—'}</div>
                    <div className="ast-lbl">Ville</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* ════ RIGHT STACK ════ */}
          <div className="right-stack">

            {/* INFOS PERSONNELLES */}
            <motion.div variants={ITEM}>
              <div className="sc">
                <div className="sc-head">
                  <div className="sc-icon" style={{background:'rgba(194,82,26,.1)',color:C.terra}}>
                    <User size={16}/>
                  </div>
                  <span className="sc-title">Informations personnelles</span>
                </div>
                <div className="sc-body">
                  <form onSubmit={handleProfile}>
                    <div className="fields-grid">
                      <Field label="Prénom"    icon={User}    name="first_name" value={form.first_name} onChange={onChange} placeholder="Votre prénom"/>
                      <Field label="Nom"       icon={User}    name="last_name"  value={form.last_name}  onChange={onChange} placeholder="Votre nom"/>
                      <Field label="Email"     icon={Mail}    name="email"      value={form.email}      onChange={onChange} type="email" placeholder="votre@email.com"/>
                      <Field label="Téléphone" icon={Phone}   name="telephone"  value={form.telephone}  onChange={onChange} placeholder="+228 90 00 00 00"/>
                      <Field label="Pays"      icon={Globe}   name="pays"       value={form.pays}       onChange={onChange} placeholder="Ex : Togo"/>
                      <Field label="Ville"     icon={MapPin}  name="ville"      value={form.ville}      onChange={onChange} placeholder="Ex : Lomé"/>
                    </div>
                    <button type="submit" className="btn-save" disabled={!!loading}>
                      {loading==='profile'
                        ? <><RefreshCw size={14} className="spin"/> Enregistrement…</>
                        : <><CheckCircle size={14}/> Enregistrer les modifications</>
                      }
                    </button>
                  </form>
                </div>
              </div>
            </motion.div>

            {/* PASSWORD + ACCOUNT — côte à côte */}
            <motion.div variants={ITEM} style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20}}>

              {/* MOT DE PASSE */}
              <div className="sc">
                <div className="sc-head">
                  <div className="sc-icon" style={{background:'rgba(26,61,40,.1)',color:C.forest}}>
                    <Lock size={16}/>
                  </div>
                  <span className="sc-title">Mot de passe</span>
                </div>
                <div className="sc-body">
                  <form onSubmit={handlePwd} style={{display:'grid',gap:14}}>
                    <Field label="Ancien mot de passe" icon={Lock} name="old_password"
                      value={pwd.old_password}
                      onChange={e=>setPwd(p=>({...p,old_password:e.target.value}))}
                      type="password" placeholder="••••••••"/>

                    <div>
                      <Field label="Nouveau mot de passe" icon={Lock} name="new_password"
                        value={pwd.new_password}
                        onChange={e=>setPwd(p=>({...p,new_password:e.target.value}))}
                        type="password" placeholder="••••••••"/>
                      {pwd.new_password && (
                        <motion.div className="pwd-strength"
                          initial={{opacity:0}} animate={{opacity:1}}>
                          <div className="strength-track">
                            <motion.div className="strength-fill"
                              style={{background:strength.color}}
                              initial={{width:0}}
                              animate={{width:`${strength.pct}%`}}
                              transition={{duration:.4,ease:[.22,1,.36,1]}}/>
                          </div>
                          <span className="strength-label" style={{color:strength.color}}>
                            {strength.label}
                          </span>
                        </motion.div>
                      )}
                    </div>

                    <button type="submit" className="btn-pwd"
                      disabled={!pwd.old_password||!pwd.new_password||!!loading}>
                      {loading==='pwd'
                        ? <><RefreshCw size={14} className="spin"/> Modification…</>
                        : <><Shield size={14}/> Modifier le mot de passe</>
                      }
                    </button>
                  </form>
                </div>
              </div>

              {/* INFOS COMPTE */}
              <div className="sc">
                <div className="sc-head">
                  <div className="sc-icon" style={{background:'rgba(200,144,42,.1)',color:C.gold}}>
                    <BadgeCheck size={16}/>
                  </div>
                  <span className="sc-title">Informations compte</span>
                </div>
                <div className="sc-body">
                  <div className="info-rows">
                    {[
                      {
                        icon: <User size={14}/>,
                        ibg: 'rgba(26,61,40,.08)', ic: C.forest,
                        label: "Nom d'utilisateur",
                        value: user?.username || '—',
                      },
                      {
                        icon: <BadgeCheck size={14}/>,
                        ibg: role.bg, ic: role.color,
                        label: 'Rôle',
                        value: role.label,
                      },
                      {
                        icon: <Mail size={14}/>,
                        ibg: 'rgba(194,82,26,.08)', ic: C.terra,
                        label: 'Email',
                        value: user?.email || '—',
                      },
                      {
                        icon: <Phone size={14}/>,
                        ibg: 'rgba(200,144,42,.1)', ic: C.gold,
                        label: 'Téléphone',
                        value: user?.telephone || '—',
                      },
                    ].map(({icon,ibg,ic,label,value})=>(
                      <div key={label} className="info-row">
                        <div className="info-row-icon" style={{background:ibg,color:ic}}>{icon}</div>
                        <div>
                          <div className="info-row-label">{label}</div>
                          <div className="info-row-value">{value}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}